import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Plus, Save, Trash2 } from 'lucide-react';
import AdminShell from '../../admin/components/layout/AdminShell';
import AdminPageHeader from '../../admin/components/shared/AdminPageHeader';
import AdminPanel from '../../admin/components/shared/AdminPanel';
import PrimaryActionButton from '../../admin/components/buttons/PrimaryActionButton';
import SecondaryActionButton from '../../admin/components/buttons/SecondaryActionButton';
import DangerActionButton from '../../admin/components/buttons/DangerActionButton';
import GhostActionButton from '../../admin/components/buttons/GhostActionButton';
import TextField from '../../admin/components/fields/TextField';
import TextareaField from '../../admin/components/fields/TextareaField';
import NumberField from '../../admin/components/fields/NumberField';
import ToggleField from '../../admin/components/fields/ToggleField';
import SelectField from '../../admin/components/fields/SelectField';
import SearchField from '../../admin/components/fields/SearchField';
import ImageUploadField from '../../admin/components/fields/ImageUploadField';
import EmptyStatePanel from '../../admin/components/shared/EmptyStatePanel';
import SmartImagePreview from '../../components/shared/SmartImagePreview';
import { useAdminContext } from '../../context/AdminContext';
import { formatPrice } from '../../utils/restaurantBranding';
import { createEmptyItem, reorderList, sortCategories, sortItems, syncItemSlug } from '../../utils/workspaceHelpers';
import { slugify } from '../../utils/slugify';
import { auditWorkspace } from '../../utils/workspaceValidation';

const AdminItemsPage: React.FC = () => {
  const { draftWorkspace, draftStatus, draftError, updateWorkspace, saveDraft, saving, uploadImage } =
    useAdminContext();
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [qualityFilter, setQualityFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const categories = useMemo(
    () => (draftWorkspace ? sortCategories(draftWorkspace.categories) : []),
    [draftWorkspace],
  );

  const workspaceAudit = useMemo(
    () => (draftWorkspace ? auditWorkspace(draftWorkspace) : null),
    [draftWorkspace],
  );

  const items = useMemo(() => {
    if (!draftWorkspace) {
      return [];
    }

    return sortItems(draftWorkspace.items).filter((item) => {
      const matchesCategory = categoryFilter === 'all' || item.categoryId === categoryFilter;
      const normalizedQuery = searchTerm.trim().toLowerCase();
      const matchesQuery =
        !normalizedQuery ||
        item.name.toLowerCase().includes(normalizedQuery) ||
        item.description.toLowerCase().includes(normalizedQuery);
      const matchesQuality =
        qualityFilter === 'all' ||
        (qualityFilter === 'missing-image' && !item.imageUrl.trim()) ||
        (qualityFilter === 'missing-description' && !item.description.trim()) ||
        (qualityFilter === 'hidden' && !item.isVisible) ||
        (qualityFilter === 'unavailable' && !item.isAvailable);

      return matchesCategory && matchesQuery && matchesQuality;
    });
  }, [categoryFilter, draftWorkspace, qualityFilter, searchTerm]);

  useEffect(() => {
    if (!selectedItemId || !items.some((item) => item.id === selectedItemId)) {
      setSelectedItemId(items[0]?.id ?? null);
    }
  }, [items, selectedItemId]);

  const selectedItem = draftWorkspace?.items.find((item) => item.id === selectedItemId);
  const mediaAssets = draftWorkspace?.mediaAssets ?? [];

  const categoryOptions = [
    { value: 'all', label: 'All Categories' },
    ...categories.map((category) => ({ value: category.id, label: category.title })),
  ];

  const qualityOptions = [
    { value: 'all', label: 'All Items' },
    { value: 'missing-image', label: 'Missing Image' },
    { value: 'missing-description', label: 'Missing Description' },
    { value: 'hidden', label: 'Hidden Items' },
    { value: 'unavailable', label: 'Unavailable Items' },
  ];

  const updateItem = (updater: (item: NonNullable<typeof selectedItem>) => NonNullable<typeof selectedItem>) => {
    if (!selectedItem) {
      return;
    }

    updateWorkspace((workspace) => ({
      ...workspace,
      items: workspace.items.map((item) => (item.id === selectedItem.id ? updater(item) : item)),
    }));
  };

  const handleAddItem = () => {
    const targetCategoryId = categoryFilter !== 'all' ? categoryFilter : categories[0]?.id;
    if (!targetCategoryId || !draftWorkspace) {
      return;
    }

    const categoryItems = draftWorkspace.items.filter((item) => item.categoryId === targetCategoryId);
    const nextItem = {
      ...createEmptyItem(targetCategoryId),
      sortOrder: categoryItems.length,
    };

    updateWorkspace((workspace) => ({
      ...workspace,
      items: [...workspace.items, nextItem],
    }));
    setSelectedItemId(nextItem.id);
  };

  const handleDeleteItem = () => {
    if (!selectedItem) {
      return;
    }

    updateWorkspace((workspace) => ({
      ...workspace,
      items: workspace.items
        .filter((item) => item.id !== selectedItem.id)
        .map((item) => {
          if (item.categoryId !== selectedItem.categoryId) {
            return item;
          }

          return {
            ...item,
            sortOrder:
              item.sortOrder > selectedItem.sortOrder ? item.sortOrder - 1 : item.sortOrder,
          };
        }),
    }));
    setSelectedItemId(null);
  };

  const moveSelectedItem = (direction: -1 | 1) => {
    if (!selectedItem || !draftWorkspace) {
      return;
    }

    const categoryItems = sortItems(
      draftWorkspace.items.filter((item) => item.categoryId === selectedItem.categoryId),
    );
    const currentIndex = categoryItems.findIndex((item) => item.id === selectedItem.id);
    const targetIndex = currentIndex + direction;

    if (targetIndex < 0 || targetIndex >= categoryItems.length) {
      return;
    }

    const reorderedCategoryItems = reorderList(categoryItems, currentIndex, targetIndex);

    updateWorkspace((workspace) => ({
      ...workspace,
      items: [
        ...workspace.items.filter((item) => item.categoryId !== selectedItem.categoryId),
        ...reorderedCategoryItems,
      ],
    }));
  };

  return (
    <AdminShell>
      <AdminPageHeader
        eyebrow="Menu Items"
        title="Edit menu items, pricing, and availability"
        description="This is the fast editing view for dish names, prices, category placement, food images, and customer visibility."
        actions={
          <>
            <SecondaryActionButton onClick={() => void saveDraft()} disabled={saving || draftStatus !== 'ready'}>
              <Save className="h-4 w-4" />
              {saving ? 'Saving...' : 'Save Draft'}
            </SecondaryActionButton>
            <PrimaryActionButton onClick={handleAddItem}>
              <Plus className="h-4 w-4" />
              Add Item
            </PrimaryActionButton>
          </>
        }
      />

      {draftError && (
        <AdminPanel className="p-4">
          <div className="rounded-2xl border border-red-400/20 bg-red-500/10 px-4 py-3 text-sm text-red-100">
            {draftError}
          </div>
        </AdminPanel>
      )}

      {!draftWorkspace || draftStatus === 'loading' ? (
        <AdminPanel className="p-8 text-center text-white/60">Loading menu items...</AdminPanel>
      ) : (
        <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
          <AdminPanel className="p-5">
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-[24px] border border-white/10 bg-white/5 px-4 py-4">
                <div className="text-[11px] uppercase tracking-[0.22em] text-white/40">Missing image</div>
                <div className="mt-2 text-2xl font-semibold text-white">{workspaceAudit?.itemsWithoutImage ?? 0}</div>
              </div>
              <div className="rounded-[24px] border border-white/10 bg-white/5 px-4 py-4">
                <div className="text-[11px] uppercase tracking-[0.22em] text-white/40">Missing detail</div>
                <div className="mt-2 text-2xl font-semibold text-white">{workspaceAudit?.itemsWithoutDescription ?? 0}</div>
              </div>
              <div className="rounded-[24px] border border-white/10 bg-white/5 px-4 py-4">
                <div className="text-[11px] uppercase tracking-[0.22em] text-white/40">Unavailable</div>
                <div className="mt-2 text-2xl font-semibold text-white">{workspaceAudit?.itemsUnavailable ?? 0}</div>
              </div>
            </div>

            <div className="mt-4 space-y-4">
              <SearchField value={searchTerm} onChange={setSearchTerm} placeholder="Search menu items..." />
              <SelectField label="Filter by Category" value={categoryFilter} options={categoryOptions} onChange={setCategoryFilter} />
              <SelectField label="Quick Quality Filter" value={qualityFilter} options={qualityOptions} onChange={setQualityFilter} />
            </div>

            <div className="mt-5 space-y-3">
              {items.map((item) => {
                const category = categories.find((entry) => entry.id === item.categoryId);
                const isSelected = item.id === selectedItemId;
                const previewImage = item.imageUrl || category?.coverImage || draftWorkspace.settings.coverImage;

                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setSelectedItemId(item.id)}
                    className={`w-full rounded-[24px] border px-4 py-4 text-left transition ${
                      isSelected
                        ? 'border-amber-300/35 bg-amber-400/12'
                        : 'border-white/10 bg-white/5 hover:bg-white/8'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <SmartImagePreview
                        src={previewImage}
                        alt={item.imageAlt || item.name}
                        className="h-24 w-24 shrink-0"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-lg font-semibold text-white">{item.name}</div>
                        <div className="mt-1 text-sm text-white/45">{category?.title ?? 'Unknown category'}</div>
                        {item.description && (
                          <div className="mt-2 line-clamp-2 text-sm leading-6 text-white/45">{item.description}</div>
                        )}
                        <div className="mt-3 flex flex-wrap gap-2 text-[11px] uppercase tracking-[0.22em] text-white/45">
                          <span className="rounded-full border border-white/10 px-2 py-1">{item.isVisible ? 'Visible' : 'Hidden'}</span>
                          <span className="rounded-full border border-white/10 px-2 py-1">{item.isAvailable ? 'Available' : 'Unavailable'}</span>
                          {!item.imageUrl.trim() && (
                            <span className="rounded-full border border-amber-300/20 bg-amber-400/10 px-2 py-1 text-amber-100">
                              Missing image
                            </span>
                          )}
                          {!item.description.trim() && (
                            <span className="rounded-full border border-amber-300/20 bg-amber-400/10 px-2 py-1 text-amber-100">
                              Missing description
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="arab-price-pill shrink-0">{formatPrice(item.price, draftWorkspace.settings)}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </AdminPanel>

          {!selectedItem ? (
            <EmptyStatePanel title="Select an item" description="Choose a menu item from the left to edit its details, price, and image." />
          ) : (
            <AdminPanel className="p-6">
              <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.3em] text-amber-100/45">Item Editor</div>
                  <div className="mt-2 text-2xl font-semibold text-white">{selectedItem.name}</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <GhostActionButton onClick={() => moveSelectedItem(-1)}>
                    <ChevronUp className="h-4 w-4" />
                  </GhostActionButton>
                  <GhostActionButton onClick={() => moveSelectedItem(1)}>
                    <ChevronDown className="h-4 w-4" />
                  </GhostActionButton>
                  <DangerActionButton onClick={handleDeleteItem}>
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </DangerActionButton>
                </div>
              </div>

              <div className="mb-6 grid gap-4 lg:grid-cols-[220px_1fr]">
                <SmartImagePreview
                  src={
                    selectedItem.imageUrl ||
                    categories.find((category) => category.id === selectedItem.categoryId)?.coverImage ||
                    draftWorkspace.settings.coverImage
                  }
                  alt={selectedItem.imageAlt || selectedItem.name}
                  className="h-52"
                />
                <div className="rounded-[24px] border border-white/10 bg-white/5 p-5">
                  <div className="text-[11px] uppercase tracking-[0.3em] text-amber-100/45">Manager Preview</div>
                  <div className="mt-3 text-2xl font-semibold text-white">{selectedItem.name}</div>
                  <div className="mt-2 text-sm text-white/55">
                    {categories.find((category) => category.id === selectedItem.categoryId)?.title ?? 'Unassigned category'}
                  </div>
                  <div className="mt-4 inline-flex arab-price-pill">{formatPrice(selectedItem.price, draftWorkspace.settings)}</div>
                  <div className="mt-4 flex flex-wrap gap-2 text-xs uppercase tracking-[0.22em] text-white/55">
                    <span className="rounded-full border border-white/10 px-3 py-1">
                      {selectedItem.isVisible ? 'Visible on site' : 'Hidden from site'}
                    </span>
                    <span className="rounded-full border border-white/10 px-3 py-1">
                      {selectedItem.isAvailable ? 'Ready to order' : 'Marked unavailable'}
                    </span>
                    {!selectedItem.imageUrl.trim() && (
                      <span className="rounded-full border border-amber-300/20 bg-amber-400/10 px-3 py-1 text-amber-100">
                        Add a dedicated image
                      </span>
                    )}
                    {!selectedItem.description.trim() && (
                      <span className="rounded-full border border-amber-300/20 bg-amber-400/10 px-3 py-1 text-amber-100">
                        Add a short description
                      </span>
                    )}
                  </div>
                  <div className="mt-4 text-sm leading-7 text-white/58">
                    {selectedItem.description || 'Add a short note so staff can quickly confirm what this item is.'}
                  </div>
                </div>
              </div>

              <div className="grid gap-5 lg:grid-cols-2">
                <TextField
                  label="Item Name"
                  value={selectedItem.name}
                  onChange={(value) =>
                    updateItem((item) => {
                      const shouldSyncSlug = item.slug === slugify(item.name);
                      return syncItemSlug({
                        ...item,
                        name: value,
                        slug: shouldSyncSlug ? value : item.slug,
                      });
                    })
                  }
                  required
                />
                <TextField
                  label="Slug"
                  value={selectedItem.slug}
                  onChange={(value) => updateItem((item) => syncItemSlug({ ...item, slug: value }))}
                />
              </div>

              <div className="mt-5 grid gap-5 lg:grid-cols-2">
                <SelectField
                  label="Category"
                  value={selectedItem.categoryId}
                  options={categories.map((category) => ({ value: category.id, label: category.title }))}
                  onChange={(value) => updateItem((item) => ({ ...item, categoryId: value }))}
                />
                <NumberField
                  label={`Price (${draftWorkspace.settings.currencySymbol || draftWorkspace.settings.currencyCode})`}
                  value={selectedItem.price}
                  onChange={(value) => updateItem((item) => ({ ...item, price: value }))}
                  min={0}
                />
              </div>

              <div className="mt-5">
                <TextareaField
                  label="Description"
                  value={selectedItem.description}
                  onChange={(value) => updateItem((item) => ({ ...item, description: value }))}
                  placeholder="Optional visible notes, ingredients, or service context"
                />
              </div>

              <div className="mt-5 grid gap-4 lg:grid-cols-2">
                <ToggleField
                  label="Visible on Public Menu"
                  value={selectedItem.isVisible}
                  onChange={(value) => updateItem((item) => ({ ...item, isVisible: value }))}
                  description="Hidden items stay in draft but disappear from the customer site."
                />
                <ToggleField
                  label="Available for Ordering"
                  value={selectedItem.isAvailable}
                  onChange={(value) => updateItem((item) => ({ ...item, isAvailable: value }))}
                  description="Unavailable items can stay visible while showing that they are currently off."
                />
              </div>

              <div className="mt-5">
                <ImageUploadField
                  label="Food Image"
                  value={selectedItem.imageUrl}
                  onChange={(value) => updateItem((item) => ({ ...item, imageUrl: value }))}
                  onAssetSelected={(asset) =>
                    updateItem((item) => ({
                      ...item,
                      imageUrl: asset.url,
                      imageAlt: item.imageAlt?.trim() ? item.imageAlt : asset.altText || item.name,
                    }))
                  }
                  assets={mediaAssets}
                  onUpload={(file) => uploadImage(file, 'item-image', selectedItem.name)}
                  helpText="Upload a food photo or reuse one from the library. If left blank, the app shows a starter image based on the item name and category."
                />
              </div>

              <div className="mt-5">
                <TextField
                  label="Image alt text (public site)"
                  value={selectedItem.imageAlt ?? ''}
                  onChange={(value) => updateItem((item) => ({ ...item, imageAlt: value }))}
                  helpText="Describe the photo for accessibility; shown when the item has an image."
                />
              </div>
            </AdminPanel>
          )}
        </div>
      )}
    </AdminShell>
  );
};

export default AdminItemsPage;
